/**
 * Admin permissions: one `area:action` string per admin section and action.
 *
 * Code defines every permission, because only code can check one. Roles
 * (`~/lib/shared/roles`) bundle permissions, and the database stores which
 * user holds which role. An area is one admin section, or a class of data
 * shown inside one; `write` includes `read`.
 *
 * Pure module, safe to import anywhere.
 *
 * Aliases: permissions, admin permissions, access control, area:action
 */

export const PERMISSION_AREAS = [
  "coaches",
  "users",
  // Giving and taking roles is its own power, apart from editing users, so a
  // later role that may edit or delete users cannot make anyone an admin.
  // Only `roles:write` is checked.
  "roles",
  "cih",
  "videos",
  // Placements decide which video plays where across the app, so a role that
  // may pick videos for a course (`videos:read`) does not also open them.
  "placements",
  "courses",
  "calls-and-links",
  "summit",
  "integrations",
  "nova-usage",
  "mpai",
  "funnel-health",
  "feature-flags",
  // Coaches' own assessments, such as the 6-Figure Readiness Score, shown
  // inside the coach directory. Holding it opens nothing alone: staff also
  // need `coaches:read`.
  "coach-assessments",
  // Updates on What's new (Admin › What's new and its MCP tools).
  "whats-new",
  // The features record (Admin › Features and its MCP tools).
  "features",
] as const;

export type PermissionArea = (typeof PERMISSION_AREAS)[number];
export type PermissionAction = "read" | "write";

/** Areas that only ever show data, so they have no `write` action. */
export const READ_ONLY_PERMISSION_AREAS = ["coach-assessments"] as const;
type ReadOnlyPermissionArea = (typeof READ_ONLY_PERMISSION_AREAS)[number];

export type Permission =
  | `${Exclude<PermissionArea, ReadOnlyPermissionArea>}:${PermissionAction}`
  | `${ReadOnlyPermissionArea}:read`;

function isReadOnlyArea(area: PermissionArea): area is ReadOnlyPermissionArea {
  return READ_ONLY_PERMISSION_AREAS.some((readOnly) => readOnly === area);
}

/** Every area at its highest action, which for `write` includes `read`. */
export const ALL_PERMISSIONS: readonly Permission[] = PERMISSION_AREAS.map(
  (area): Permission =>
    isReadOnlyArea(area) ? `${area}:read` : `${area}:write`,
);

export interface RoleDefinition {
  label: string;
  /** What the role opens, as one line under its name in Manage roles. */
  description: string;
  /**
   * What the role opens, to finish "can then open …" and "loses access to …"
   * in the confirmations.
   */
  opens: string;
  permissions: readonly Permission[];
}

/** True when `granted` holds `required`, or its `write` for a `read`. */
export function hasPermission(
  granted: readonly Permission[],
  required: Permission,
): boolean {
  if (granted.includes(required)) return true;
  if (!required.endsWith(":read")) return false;
  const write = `${required.slice(0, -":read".length)}:write`;
  return granted.some((permission) => permission === write);
}

/**
 * The permissions the given stored role keys grant. A stored key that no role
 * defines grants nothing.
 */
export function permissionsForRoles(
  roleKeys: readonly string[],
  roles: Readonly<Record<string, RoleDefinition>>,
): Permission[] {
  const granted = new Set<Permission>();
  for (const key of roleKeys) {
    if (!Object.hasOwn(roles, key)) continue;
    for (const permission of roles[key].permissions) granted.add(permission);
  }
  return [...granted];
}

/**
 * The navigation entries a person may see: an entry without a permission
 * (the dashboard) always shows, a group keeps only the children the person
 * may read, and a group with none left hides.
 */
export function navItemsVisibleTo<
  Child extends { permission: Permission },
  Item extends { permission?: Permission; children?: Child[] },
>(items: readonly Item[], granted: readonly Permission[]): Item[] {
  return items.flatMap((item): Item[] => {
    if (item.children) {
      const children = item.children.filter((child) =>
        hasPermission(granted, child.permission),
      );
      if (children.length === 0) return [];
      return [{ ...item, children }];
    }
    if (item.permission && !hasPermission(granted, item.permission)) return [];
    return [item];
  });
}
